/**
 * Per-request store context, for SSR only.
 *
 * The composable store pattern we're migrating to (see ./defineState.ts) keeps state in
 * module-scope singletons. That is safe in a browser — one page, one user — but on the
 * server the module graph is shared by every concurrent request, so a bare singleton
 * would leak one user's session into another's response.
 *
 * `app-reviewer` genuinely populates per-user state during SSR: its `routeGuard` boot file
 * is not marked `server: false` and does `AUTH_LOAD`, which reads the request's cookies.
 * So request scoping is a correctness requirement, not a precaution.
 *
 * AsyncLocalStorage gives each request its own context, established by
 * `runInRequestContext` — which WRAPS the render, so every continuation of it is inside
 * the store.
 *
 * It used to be `enterWith`, called from the per-request store factory Quasar 1 demanded:
 * that factory returns synchronously and so could not wrap anything. It also does not
 * work. `enterWith` binds the store to the current async resource, and the render for the
 * SECOND request onwards runs on a resource that predates the call — so only the first
 * request of a server's life had a context and every one after it died with the error
 * below. Quasar 2 renders from `src-ssr/middlewares/render.ts`, which is ours, so the
 * render can be wrapped properly.
 *
 * Everything here is server-only. Webpack's DefinePlugin folds `process.env.SERVER` to a
 * literal, so the browser bundle drops these branches entirely.
 */

export interface StoreRequestContext {
  ssrContext: any
  states: { [name: string]: object }
  /**
   * Per-request cookie jar, populated lazily by `cookiesModule` from `Cookies.parseSSR`. Lives
   * here rather than in its own mechanism because it needs exactly the same request scoping as
   * state, and the ssrContext it is parsed from is already here.
   */
  cookies?: any
  /**
   * Per-request VueI18n instance, set by `i18nModule.init` from each app's i18n boot file. The
   * reader can pick a language, so two requests being rendered at the same moment can be in two
   * different locales, and a module-scope instance would render one reader's page in the other's
   * language. Not a `defineState` slot: an i18n instance must not be wrapped in `reactive()`.
   */
  i18n?: any
}

/**
 * The AsyncLocalStorage, kept on `globalThis`.
 *
 * A module-scope instance is not enough: an SSR build has TWO copies of this file. The app is a
 * webpack bundle and the webserver that wraps the render is an esbuild bundle, and the middleware
 * has to import this to scope the request -- so the render entered a context in one copy while the
 * app read from the other, and every request died with the error below.
 *
 * Required lazily so the browser bundle never resolves `async_hooks`.
 */
const ASYNC_LOCAL_STORAGE_KEY = '__booksproutStoreAsyncLocalStorage'

const getAsyncLocalStorage = () => {
  const globals = globalThis as unknown as Record<string, any>

  if (!globals[ASYNC_LOCAL_STORAGE_KEY]) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { AsyncLocalStorage } = require('async_hooks')
    globals[ASYNC_LOCAL_STORAGE_KEY] = new AsyncLocalStorage()
  }

  return globals[ASYNC_LOCAL_STORAGE_KEY]
}

/**
 * Run `fn` with a fresh store context for one request, and return whatever it returns.
 *
 * Called from each SSR app's render middleware, around the whole render — so the boot files, the
 * render itself and the state serialisation that follows it all see the same context.
 *
 * A no-op on the client: there is one page and one user, so the module-scope singletons in
 * `defineState` are already per-visitor.
 */
export const runInRequestContext = <T> (ssrContext: any, fn: (context: StoreRequestContext) => T): T => {
  if (!process.env.SERVER) {
    return fn({ ssrContext, states: {} })
  }

  const context: StoreRequestContext = {
    ssrContext,
    states: {}
  }

  return getAsyncLocalStorage().run(context, () => fn(context))
}

export const getRequestContext = (): StoreRequestContext => {
  const context = getAsyncLocalStorage().getStore()

  if (!context) {
    /**
     * Reaching here means state was touched on the server outside a request — most likely
     * at module-import time. Failing loudly beats silently sharing state between users.
     */
    throw new Error(
      '[store] No SSR request context. State was accessed outside a request; ' +
      'runInRequestContext() wraps the render (see src-ssr/middlewares/render.ts) and must be ' +
      'entered before any state is read.'
    )
  }

  return context
}

/**
 * Test seam: the same thing without the `process.env.SERVER` guard, so a spec can establish a
 * context whichever side it is pretending to be on.
 */
export const _runInRequestContext = <T> (ssrContext: any, fn: (context: StoreRequestContext) => T): T => {
  const context: StoreRequestContext = {
    ssrContext,
    states: {}
  }

  return getAsyncLocalStorage().run(context, () => fn(context))
}
